/**
 * Tape Profile — session-level trade distribution below the intraday chart.
 */

import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { stockService, type TradeSessionStatsPayload } from '@/services/api';
import {
  buildTapeProfileHistogram,
  sortTapeProfileHistogramRows,
  TAPE_PROFILE_HISTOGRAM_SORT_OPTIONS,
  type TapeProfileHistogramSort,
  fmtCompact,
  fmtPct,
  fmtPrice,
} from '@/utils/tradeSessionStatsDisplay';

type Props = {
  ticker: string;
  date: string;
  className?: string;
};

export function TradeSessionProfile({ ticker, date, className = '' }: Props) {
  const [data, setData] = useState<TradeSessionStatsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [histogramSort, setHistogramSort] = useState<TapeProfileHistogramSort>('price-desc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    stockService.getTradeSessionStats(ticker, date).then((result) => {
      if (cancelled) return;
      setData(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [ticker, date]);

  useEffect(() => {
    setHistogramSort('price-desc');
    setIsOpen(false);
  }, [ticker, date]);

  const rth = data?.regular_hours;
  const histogramResult = useMemo(
    () => (rth ? buildTapeProfileHistogram(rth) : null),
    [rth],
  );
  const histogram = useMemo(() => {
    if (!histogramResult) return [];
    return sortTapeProfileHistogramRows(histogramResult.rows, histogramSort);
  }, [histogramResult, histogramSort]);

  if (!loading && (!data?.success || !rth?.total_trades)) {
    return null;
  }

  const tickCount = data?.deduped_trade_count;

  return (
    <div className={`rounded-lg border border-gray-100 bg-white overflow-hidden ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
      >
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Tape profile
          {tickCount != null && (
            <span className="normal-case font-normal text-gray-400 ml-1.5">
              (regular session · {fmtCompact(tickCount)} ticks analyzed)
            </span>
          )}
          {loading && (
            <span className="normal-case font-normal text-gray-400 ml-1.5">
              Loading…
            </span>
          )}
        </p>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && loading && (
        <div className="px-4 pb-3 border-t border-gray-50">
          <p className="pt-3 text-xs text-gray-400">Loading tape profile…</p>
        </div>
      )}

      {isOpen && !loading && histogram.length > 0 && histogramResult && (
        <div className="px-4 py-3 border-t border-gray-50">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Volume by price
            </p>
            <div className="flex flex-wrap gap-1.5">
              {TAPE_PROFILE_HISTOGRAM_SORT_OPTIONS.map((opt) => {
                const active = histogramSort === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setHistogramSort(opt.id)}
                    className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                      active
                        ? 'bg-slate-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-[11px] text-gray-500 mb-2 leading-snug">
            {fmtCompact(histogramResult.levelCount)} price levels with trades ·{' '}
            <span className="font-medium text-gray-700">
              {fmtCompact(histogramResult.totalVolume)} shares
            </span>
            {' · '}
            <span className="font-medium text-gray-700">
              {fmtCompact(histogramResult.totalTrades)} prints
            </span>
          </p>
          <div className="max-h-[420px] overflow-y-auto rounded-md border border-gray-100 bg-gray-50/40 pr-1">
            <ul className="py-1">
              {histogram.map((row, idx) => {
                if (row.kind === 'marker') {
                  return (
                    <li
                      key={`marker-${row.label}-${row.price}`}
                      className="px-2 py-0.5 text-[11px] font-semibold text-purple-700 tabular-nums border-y border-purple-100/80 bg-purple-50/60"
                    >
                      {row.label} = {row.price.toFixed(2)}
                    </li>
                  );
                }

                return (
                  <li
                    key={`bar-${row.price}-${idx}`}
                    className="grid grid-cols-[3.75rem_1fr_4.25rem_4.25rem] gap-x-2 items-center px-2 py-[3px] text-[10px]"
                  >
                    <span className="text-gray-600 tabular-nums">{fmtPrice(row.price)}</span>
                    <div
                      className="min-w-0 h-2 rounded-sm bg-gray-100 overflow-hidden"
                      title={`${fmtCompact(row.volume)} shares (${fmtPct(row.pctOfSessionVolume, 1)} of session) · ${fmtCompact(row.tradeCount)} prints (${fmtPct(row.pctOfSessionTrades, 1)} of session)`}
                    >
                      <div
                        className="h-full rounded-sm bg-emerald-500/80"
                        style={{ width: `${Math.max(row.barPct, 1.5)}%` }}
                      />
                    </div>
                    <span className="text-gray-600 tabular-nums text-right leading-tight">
                      {fmtCompact(row.volume)}
                      <span className="block text-[9px] text-gray-400">
                        {fmtPct(row.pctOfSessionVolume, 1)} vol
                      </span>
                    </span>
                    <span className="text-gray-600 tabular-nums text-right leading-tight">
                      {fmtCompact(row.tradeCount)}
                      <span className="block text-[9px] text-gray-400">
                        {fmtPct(row.pctOfSessionTrades, 1)} prints
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
